import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import {
  type StripeCardCvcElementChangeEvent,
  type StripeCardExpiryElementChangeEvent,
  type StripeCardNumberElementChangeEvent,
} from '@stripe/stripe-js';
import {
  useState,
  type CSSProperties,
  type ChangeEvent as ReactChangeEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { useDispatch } from 'react-redux';
import { addPayment, showMessage } from '../../actions';
import CvcCardIcon from '../../images/cvc-card.svg';
import { Colors } from '../../themes/colors';
import '../../themes/payment.scss';
import { Button } from '@tempmee/react';

interface ICardFormProps {
  /** Callback function triggered when card is successfully added and tokenized */
  onSuccess: () => void;
}

/**
 * CardForm Component
 *
 * A Stripe-powered payment form for collecting and tokenizing credit card information.
 * Handles secure card input validation, ZIP code collection, and payment token creation.
 *
 * ⚠️  DEVELOPER NOTE: This component is currently located in commonComponents
 * but appears to be used only for payment processing in the CardList component.
 * Consider whether this should be moved to a more specific location like:
 * - src/components/Payments/components/
 * - src/domain/payments/components/
 *
 * If this is truly only used for payment workflows, it may not need
 * to be a shared component.
 *
 * @example
 * ```tsx
 * <CardForm onSuccess={() => console.log('Card added successfully')} />
 * ```
 */
export const CardForm = ({ onSuccess }: ICardFormProps) => {
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();

  const [isCardNumberComplete, setCardNumberComplete] = useState<boolean>(false);
  const [isCardExpiryComplete, setCardExpiryComplete] = useState<boolean>(false);
  const [isCardCvcComplete, setCardCvcComplete] = useState<boolean>(false);
  const [zipCode, setZipCode] = useState<string>('');

  const isSubmitDisabled = !(
    isCardNumberComplete &&
    isCardExpiryComplete &&
    isCardCvcComplete &&
    zipCode.length === 5
  );

  const handleZipChange = (value: string): void => {
    if (/^\d{0,5}$/.test(value)) {
      setZipCode(value);
    }
  };

  const handleSubmit = async (e: ReactMouseEvent<HTMLButtonElement>): Promise<void> => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);

    if (!cardElement) {
      return;
    }

    const { token, error } = await stripe.createToken(cardElement, {
      currency: 'usd',
      address_country: 'US',
      address_zip: zipCode,
    });

    if (error) {
      dispatch(showMessage({ title: 'Error', message: error.message, isError: true }));
    } else if (token) {
      dispatch(addPayment({ card: { token } }));
      onSuccess();
    }
  };

  const handleCardNumberChange = (event: StripeCardNumberElementChangeEvent): void => {
    setCardNumberComplete(event.complete);
    if (event.complete) {
      const expiryElement = elements?.getElement(CardExpiryElement);
      if (expiryElement) {
        expiryElement.focus();
      }
    }
  };

  const handleCardExpiryChange = (event: StripeCardExpiryElementChangeEvent): void => {
    setCardExpiryComplete(event.complete);
    if (event.complete) {
      const cvcElement = elements?.getElement(CardCvcElement);
      if (cvcElement) {
        cvcElement.focus();
      }
    }
  };

  const handleCardCvcChange = (event: StripeCardCvcElementChangeEvent): void => {
    setCardCvcComplete(event.complete);
  };

  const handleZipInputChange = (event: ReactChangeEvent<HTMLInputElement>): void => {
    handleZipChange(event.target.value);
  };

  return (
    <div className="payment-form" data-cy="stripe-card-form" data-testid="stripe-card-form">
      <div
        style={{
          boxSizing: 'content-box',
          width: '100%',
          backgroundColor: Colors.neutral_50,
          marginLeft: -40,
          padding: '40px 40px 60px 40px',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div data-cy="input-card-number" data-testid="input-card-number" style={styles.container}>
            <span style={styles.title}>Card Number</span>
            <CardNumberElement
              id="cardnumber"
              options={{
                showIcon: true,
                style: styles.cardInput,
              }}
              onChange={handleCardNumberChange}
            />
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 12,
            }}
          >
            <div
              data-cy="input-card-expiration"
              data-testid="input-card-expiration"
              style={{ ...styles.container, width: '50%' }}
            >
              <span style={styles.title}>Expiration</span>
              <CardExpiryElement
                id="exp-date"
                options={{
                  style: styles.cardInput,
                }}
                onChange={handleCardExpiryChange}
              />
            </div>
            <div
              data-cy="input-card-cvc"
              data-testid="input-card-cvc"
              style={{ ...styles.container, width: '50%' }}
            >
              <span style={styles.title}>CVC</span>
              <div style={{ position: 'relative' }}>
                <CardCvcElement
                  id="cvc"
                  options={{
                    style: styles.cardInput,
                  }}
                  onChange={handleCardCvcChange}
                />
                <img
                  src={CvcCardIcon}
                  alt="CVC"
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '24px',
                    height: '24px',
                  }}
                />
              </div>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 12,
            }}
          >
            <div style={{ ...styles.container, width: '50%' }}>
              <span style={styles.title}>Country</span>
              <input
                value="United States"
                style={{
                  fontSize: 16,
                  color: Colors.neutral_600,
                  backgroundColor: Colors.neutral_70,
                  paddingTop: 11,
                  paddingBottom: 11,
                }}
                disabled
                readOnly
              />
            </div>
            <div
              data-cy="input-card-zip"
              data-testid="input-card-zip"
              style={{ ...styles.container, width: '50%' }}
            >
              <span style={styles.title}>ZIP</span>
              <input
                id="zip"
                value={zipCode}
                style={{
                  fontSize: 16,
                  color: Colors.neutral_600,
                  paddingTop: 11,
                  paddingBottom: 11,
                }}
                placeholder="12345"
                onChange={handleZipInputChange}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Button
          data-cy="btn-add-card"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          size="md"
          style={{ marginTop: 40, marginBottom: -72 }}
        >
          Add Card
        </Button>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
  } as CSSProperties,
  title: {
    fontFamily: 'Nunito',
    fontSize: 16,
    fontWeight: 700,
    color: Colors.neutral_600,
    textAlign: 'left',
  } as CSSProperties,
  cardInput: {
    base: {
      fontSize: '16px',
      WebkitFontSmoothing: 'antialiased',
      color: Colors.neutral_600,
      '::placeholder': {
        color: Colors.neutral_300,
      },
    },
  },
};
